import { useMemo, useState } from "react";
import { Braces, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Button, Input, NativeSelect } from "@ncp/ui";
import { CodeEditor } from "../../components/CodeEditor";

export interface ConditionField { key: string; label: string; type?: string; options?: Array<{ value: string; label: string }> }

export type ConditionOp = "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "empty" | "filled" | "contains" | "oneOf";
export interface ConditionRow { field: string; op: ConditionOp; value: string }
export interface Condition { join: "and" | "or"; rows: ConditionRow[] }

export const CONDITION_OPS: ConditionOp[] = ["eq", "neq", "gt", "gte", "lt", "lte", "empty", "filled", "contains", "oneOf"];
const JL: Partial<Record<ConditionOp, string>> = { eq: "==", neq: "!=", gt: ">", gte: ">=", lt: "<", lte: "<=" };
const NUMERIC = new Set(["number", "money", "integer"]);
const NO_VALUE = new Set<ConditionOp>(["empty", "filled"]);

type Json = null | boolean | number | string | Json[] | { [k: string]: Json };

function typed(value: string, type?: string): Json {
  if (type && NUMERIC.has(type)) { const n = Number(value); return Number.isFinite(n) ? n : value; }
  if (type === "boolean") return value === "true";
  return value;
}

/** A row list as JsonLogic. */
export function toJsonLogic(c: Condition, fields: ConditionField[]): Json {
  const typeOf = (k: string) => fields.find((f) => f.key === k)?.type;
  const parts: Json[] = c.rows.filter((r) => r.field).map((r): Json => {
    const v: Json = { var: r.field };
    if (r.op === "empty") return { "!": v };
    if (r.op === "filled") return { "!!": v };
    if (r.op === "contains") return { in: [r.value, v] };
    if (r.op === "oneOf") return { in: [v, r.value.split(",").map((x) => typed(x.trim(), typeOf(r.field)))] };
    return { [JL[r.op]!]: [v, typed(r.value, typeOf(r.field))] };
  });
  if (parts.length === 0) return true;
  return parts.length === 1 ? parts[0]! : { [c.join]: parts };
}

function varOf(n: unknown): string | null {
  return n && typeof n === "object" && !Array.isArray(n) && typeof (n as { var?: unknown }).var === "string" ? (n as { var: string }).var : null;
}

function rowOf(expr: unknown): ConditionRow | null {
  if (!expr || typeof expr !== "object" || Array.isArray(expr)) return null;
  const [op, arg] = Object.entries(expr as Record<string, unknown>)[0] ?? [];
  if (!op) return null;
  if (op === "!" || op === "!!") { const f = varOf(Array.isArray(arg) ? arg[0] : arg); return f ? { field: f, op: op === "!" ? "empty" : "filled", value: "" } : null; }
  if (!Array.isArray(arg) || arg.length !== 2) return null;
  if (op === "in") {
    const left = varOf(arg[0]); const right = varOf(arg[1]);
    if (right && (typeof arg[0] === "string")) return { field: right, op: "contains", value: arg[0] };
    if (left && Array.isArray(arg[1])) return { field: left, op: "oneOf", value: (arg[1] as unknown[]).map(String).join(", ") };
    return null;
  }
  const mapped = (Object.entries(JL).find(([, v]) => v === op)?.[0]) as ConditionOp | undefined;
  const f = varOf(arg[0]);
  if (!mapped || !f || (arg[1] !== null && typeof arg[1] === "object")) return null;
  return { field: f, op: mapped, value: String(arg[1] ?? "") };
}

/** JsonLogic back to rows, or null when it is not a flat list the builder can show. */
export function fromJsonLogic(expr: unknown): Condition | null {
  if (expr === true || expr == null) return { join: "and", rows: [] };
  if (expr && typeof expr === "object" && !Array.isArray(expr)) {
    const [op, arg] = Object.entries(expr as Record<string, unknown>)[0] ?? [];
    if ((op === "and" || op === "or") && Array.isArray(arg)) {
      const rows = arg.map(rowOf);
      return rows.every(Boolean) ? { join: op, rows: rows as ConditionRow[] } : null;
    }
  }
  const row = rowOf(expr);
  return row ? { join: "and", rows: [row] } : null;
}

/** A sentence for a condition: "Estimated cost is above 5000 and Sector is filled". */
export function describeCondition(expr: unknown, fields: ConditionField[], t: (k: string, o?: Record<string, unknown>) => string): string {
  const c = fromJsonLogic(expr);
  if (!c) return t("admin:generic.condition.custom");
  if (c.rows.length === 0) return t("admin:generic.condition.always");
  const label = (k: string) => fields.find((f) => f.key === k)?.label ?? k;
  return c.rows.map((r) => `${label(r.field)} ${t(`admin:generic.condition.ops.${r.op}`)}${NO_VALUE.has(r.op) ? "" : ` ${r.value}`}`).join(` ${t(`admin:generic.condition.${c.join}`)} `);
}

interface Props { value: unknown; onChange: (next: Json) => void; fields: ConditionField[]; readOnly?: boolean; testId?: string }

/** Plain-language condition editor: "when [field] [is above] [value]" rows joined by all/any, with a JSON view for anything more complex. */
export function ConditionBuilder({ value, onChange, fields, readOnly, testId }: Props) {
  const { t } = useTranslation();
  const parsed = useMemo(() => fromJsonLogic(value), [value]);
  const [raw, setRaw] = useState(!parsed);
  const cond: Condition = parsed ?? { join: "and", rows: [] };
  const set = (next: Condition) => onChange(toJsonLogic(next, fields));
  const setRow = (i: number, p: Partial<ConditionRow>) => set({ ...cond, rows: cond.rows.map((r, j) => (j === i ? { ...r, ...p } : r)) });

  if (raw) {
    return (
      <div className="space-y-2">
        <CodeEditor value={JSON.stringify(value ?? true, null, 2)} language="json" readOnly={readOnly} height="160px" onChange={(v) => { try { onChange(JSON.parse(v) as Json); } catch { /* keep typing */ } }} />
        {parsed || fromJsonLogic(value) ? <Button size="sm" variant="ghost" onClick={() => setRaw(false)}>{t("admin:generic.condition.simple")}</Button> : null}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={testId}>
      {cond.rows.length > 1 ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {t("admin:generic.condition.match")}
          <NativeSelect value={cond.join} disabled={readOnly} className="h-8 w-40" onChange={(e) => set({ ...cond, join: e.target.value as "and" | "or" })}>
            <option value="and">{t("admin:generic.condition.allOf")}</option>
            <option value="or">{t("admin:generic.condition.anyOf")}</option>
          </NativeSelect>
        </div>
      ) : null}
      {cond.rows.map((r, i) => {
        const field = fields.find((f) => f.key === r.field);
        return (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <NativeSelect aria-label={t("admin:generic.condition.field")} value={r.field} disabled={readOnly} className="h-8 w-48" onChange={(e) => setRow(i, { field: e.target.value })}>
              <option value="">—</option>
              {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </NativeSelect>
            <NativeSelect aria-label={t("admin:generic.condition.operator")} value={r.op} disabled={readOnly} className="h-8 w-40" onChange={(e) => setRow(i, { op: e.target.value as ConditionOp })}>
              {CONDITION_OPS.map((op) => <option key={op} value={op}>{t(`admin:generic.condition.ops.${op}`)}</option>)}
            </NativeSelect>
            {NO_VALUE.has(r.op) ? null : field?.options?.length && r.op !== "oneOf" ? (
              <NativeSelect aria-label={t("admin:generic.condition.value")} value={r.value} disabled={readOnly} className="h-8 w-44" onChange={(e) => setRow(i, { value: e.target.value })}>
                <option value="">—</option>
                {field.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </NativeSelect>
            ) : field?.type === "boolean" ? (
              <NativeSelect aria-label={t("admin:generic.condition.value")} value={r.value} disabled={readOnly} className="h-8 w-32" onChange={(e) => setRow(i, { value: e.target.value })}>
                <option value="true">{t("admin:common.yes")}</option><option value="false">{t("admin:common.no")}</option>
              </NativeSelect>
            ) : (
              <Input aria-label={t("admin:generic.condition.value")} value={r.value} disabled={readOnly} className="h-8 w-44" inputMode={field?.type && NUMERIC.has(field.type) ? "decimal" : undefined} placeholder={r.op === "oneOf" ? "a, b, c" : undefined} onChange={(e) => setRow(i, { value: e.target.value })} />
            )}
            {!readOnly ? <Button size="sm" variant="ghost" aria-label={t("admin:common.remove")} onClick={() => set({ ...cond, rows: cond.rows.filter((_, j) => j !== i) })}><Trash2 /></Button> : null}
          </div>
        );
      })}
      <div className="flex gap-1">
        {!readOnly ? <Button size="sm" variant="outline" onClick={() => set({ ...cond, rows: [...cond.rows, { field: fields[0]?.key ?? "", op: "eq", value: "" }] })}><Plus /> {t("admin:generic.condition.add")}</Button> : null}
        <Button size="sm" variant="ghost" onClick={() => setRaw(true)}><Braces /> {t("admin:generic.condition.json")}</Button>
      </div>
    </div>
  );
}
